import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArcaPdfStudioLayout from '../../components/arca/ArcaPdfStudioLayout';
import { appendUploadFiles, isPdfFile } from '../../components/arca/uploadItemUtils';
import { apiFetch, getToken } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { withUnidade } from '../../utils/storageByUnit';
import API_CONFIG from '../../config/api';
import { MESES_COMPETENCIA, montarCompetencia } from '../../utils/competenciaUtils';

function formatMoney(v) {
  if (v == null || v === '') return '—';
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatDate(d) {
  if (!d) return '—';
  const dt = new Date(String(d).includes('T') ? d : `${d}T12:00:00`);
  return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString('pt-BR');
}

function parseMoneyInput(value) {
  if (value == null || value === '') return null;
  const n = parseFloat(String(value).replace(/\./g, '').replace(',', '.'));
  return Number.isNaN(n) ? null : n;
}

function normSearch(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function contratoSearchBlob(c) {
  return normSearch(
    [
      c.numeroContrato,
      c.empresa,
      c.titulo,
      c.objeto,
      c.especialidade,
      c.descricao,
      c.tipo,
      c.tipoServico,
      c.codigoServico,
      c.setor,
      c.servicoMedico ? 'servico medico serviço médico' : '',
    ].join(' ')
  );
}

function labelContrato(c) {
  if (!c) return '';
  const parts = [c.numeroContrato, c.empresa || c.titulo];
  if (c.objeto) parts.push(String(c.objeto).slice(0, 60));
  if (c.servicoMedico) parts.push('Serviço médico');
  return parts.filter(Boolean).join(' — ');
}

function leadingDocNumber(nome) {
  const m = String(nome || '').match(/^\s*(\d+)\s*[\.\-_\)]/);
  return m ? parseInt(m[1], 10) : 9999;
}

function sortItemsByDocNumber(list) {
  return [...(list || [])].sort((a, b) => {
    const na = leadingDocNumber(a.file?.name);
    const nb = leadingDocNumber(b.file?.name);
    if (na !== nb) return na - nb;
    return String(a.file?.name || '').localeCompare(String(b.file?.name || ''), 'pt-BR', {
      numeric: true,
      sensitivity: 'base',
    });
  });
}

function baixarBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const emptyForm = {
  numeroNotaFiscal: '',
  dataEmissao: '',
  dataRecebimento: '',
  valorBruto: '',
  valorLiquido: '',
  mesCompetencia: '',
  anoCompetencia: String(new Date().getFullYear()),
};

export default function ArcaPdfProcessoPagamentoPage() {
  const [searchParams] = useSearchParams();
  const { unidadeSelecionada, user } = useAuth();

  const [contratos, setContratos] = useState([]);
  const [contratoOpt, setContratoOpt] = useState(null);
  const [contratoInput, setContratoInput] = useState('');

  const [modo, setModo] = useState('novo'); // 'novo' | 'existente'
  const [notas, setNotas] = useState([]);
  const [notaId, setNotaId] = useState(searchParams.get('notaId') || '');
  const [docsExistentes, setDocsExistentes] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [items, setItems] = useState([]);

  const [loadingContratos, setLoadingContratos] = useState(false);
  const [loadingNotas, setLoadingNotas] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [usuarios, setUsuarios] = useState([]);
  const [signatariosSel, setSignatariosSel] = useState([]);
  const [emailsExternos, setEmailsExternos] = useState('');
  const [cadeiaPreview, setCadeiaPreview] = useState(null);
  const [loadingCadeia, setLoadingCadeia] = useState(false);

  const contratoSel = contratoOpt?.numeroContrato || '';

  const notaSelecionada = useMemo(
    () => notas.find((n) => String(n.id) === String(notaId)) || null,
    [notas, notaId]
  );

  const contratosFiltrados = useMemo(() => {
    const q = normSearch(contratoInput);
    if (!q) return contratos.slice(0, 80);
    const tokens = q.split(/\s+/).filter(Boolean);
    return contratos
      .filter((c) => {
        const blob = contratoSearchBlob(c);
        return tokens.every((t) => blob.includes(t));
      })
      .slice(0, 80);
  }, [contratos, contratoInput]);

  const loadContratos = useCallback(async () => {
    setLoadingContratos(true);
    try {
      const path = withUnidade('/api/contratos', unidadeSelecionada);
      const res = await apiFetch(path);
      if (!res.ok) throw new Error('Falha ao carregar contratos.');
      const data = await res.json();
      setContratos(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message || 'Erro ao carregar contratos.');
      setContratos([]);
    } finally {
      setLoadingContratos(false);
    }
  }, [unidadeSelecionada]);

  useEffect(() => {
    loadContratos();
  }, [loadContratos]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await apiFetch(withUnidade('/api/usuarios', unidadeSelecionada));
        const json = await res.json().catch(() => []);
        const list = Array.isArray(json) ? json : json.usuarios || json.items || [];
        if (!cancelled) setUsuarios(list);
      } catch {
        if (!cancelled) setUsuarios([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [unidadeSelecionada]);

  const userOptions = useMemo(
    () =>
      (usuarios || []).map((u) => ({
        id: u.id,
        nome: u.nome || u.name || u.email,
        email: u.email,
        setor: u.setor || '',
        label: `${u.nome || u.email}${u.setor ? ` · ${u.setor}` : ''}`,
      })),
    [usuarios]
  );

  const montarSignatariosPayload = () => {
    const fromUsers = (signatariosSel || [])
      .filter((u) => u?.email)
      .map((u) => ({
        nome: u.nome || u.label || u.email,
        email: String(u.email).trim().toLowerCase(),
        userId: u.id || null,
        externo: false,
      }));
    const fromExt = String(emailsExternos || '')
      .split(/[;,]/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.includes('@'))
      .map((email) => ({
        nome: email.split('@')[0],
        email,
        userId: null,
        externo: true,
      }));
    const seen = new Set();
    return [...fromUsers, ...fromExt].filter((s) => {
      if (seen.has(s.email)) return false;
      seen.add(s.email);
      return true;
    });
  };

  // Preview da cadeia: Fluxo de Pagamento + fiscal do contrato
  useEffect(() => {
    if (!contratoSel) {
      setCadeiaPreview(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingCadeia(true);
      try {
        const extras = montarSignatariosPayload();
        const qs = new URLSearchParams({
          numeroContrato: contratoSel,
          signatarios: JSON.stringify(extras),
        });
        const path = withUnidade(
          `/api/arca/pdf/processo-pagamento/cadeia-preview?${qs.toString()}`,
          unidadeSelecionada
        );
        const res = await apiFetch(path);
        const data = await res.json().catch(() => ({}));
        if (!cancelled) setCadeiaPreview(res.ok ? data : null);
      } catch {
        if (!cancelled) setCadeiaPreview(null);
      } finally {
        if (!cancelled) setLoadingCadeia(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoSel, unidadeSelecionada, signatariosSel, emailsExternos]);

  // Prefill da URL (atalho Notas Fiscais)
  useEffect(() => {
    const qNota = searchParams.get('notaId');
    const qContrato = searchParams.get('numeroContrato');
    if (qContrato && contratos.length) {
      const found = contratos.find((c) => String(c.numeroContrato) === String(qContrato));
      if (found) {
        setContratoOpt(found);
        setContratoInput(labelContrato(found));
      }
    }
    if (qNota) {
      setNotaId(qNota);
      setModo('existente');
    }
  }, [searchParams, contratos]);

  useEffect(() => {
    if (!contratoSel) {
      setNotas([]);
      if (modo === 'existente') setNotaId('');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingNotas(true);
      try {
        const path = withUnidade(
          `/api/notas-fiscais/${encodeURIComponent(contratoSel)}`,
          unidadeSelecionada
        );
        const res = await apiFetch(path);
        if (!res.ok) throw new Error('Falha ao carregar notas do contrato.');
        const data = await res.json();
        if (!cancelled) setNotas(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          setNotas([]);
          setError(e.message || 'Erro ao carregar notas.');
        }
      } finally {
        if (!cancelled) setLoadingNotas(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [contratoSel, unidadeSelecionada, modo]);

  useEffect(() => {
    if (modo !== 'existente' || !notaId) {
      setDocsExistentes([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoadingDocs(true);
      try {
        const path = withUnidade(`/api/notas-fiscais/${notaId}/documentos`, unidadeSelecionada);
        const res = await apiFetch(path);
        if (!res.ok) throw new Error('Falha ao carregar documentos da NF.');
        const data = await res.json();
        if (!cancelled) {
          const list = Array.isArray(data) ? data : [];
          setDocsExistentes(
            [...list].sort((a, b) => {
              const na = leadingDocNumber(a.nome_arquivo);
              const nb = leadingDocNumber(b.nome_arquivo);
              if (na !== nb) return na - nb;
              return String(a.nome_arquivo || '').localeCompare(String(b.nome_arquivo || ''), 'pt-BR', {
                numeric: true,
                sensitivity: 'base',
              });
            })
          );
        }
      } catch (e) {
        if (!cancelled) setDocsExistentes([]);
      } finally {
        if (!cancelled) setLoadingDocs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modo, notaId, unidadeSelecionada]);

  const handleFilesAdd = (files) => {
    setError('');
    setSuccess('');
    const setItemsSorted = (updater) => {
      setItems((prev) => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        return sortItemsByDocNumber(next);
      });
    };
    const n = appendUploadFiles(files, setItemsSorted, { multiple: true, filter: isPdfFile });
    if (!n) setError('Selecione apenas PDFs.');
  };

  const criarNotaSeNecessario = async () => {
    if (modo === 'existente' && notaId) return Number(notaId);
    if (!contratoOpt) throw new Error('Selecione um contrato.');
    if (!form.numeroNotaFiscal.trim()) throw new Error('Informe o nº da nota fiscal.');
    if (!form.dataEmissao) throw new Error('Informe a data de emissão.');
    if (!form.mesCompetencia || !form.anoCompetencia) {
      throw new Error('Informe mês e ano da competência.');
    }
    const competencia = montarCompetencia(form.mesCompetencia, form.anoCompetencia);
    if (!competencia) throw new Error('Competência inválida. Selecione mês e ano.');
    const payload = {
      numeroContrato: contratoOpt.numeroContrato,
      empresa: contratoOpt.empresa || contratoOpt.titulo || '',
      numeroNotaFiscal: form.numeroNotaFiscal.trim(),
      dataEmissao: form.dataEmissao,
      dataRecebimento: form.dataRecebimento || form.dataEmissao,
      valorBruto: parseMoneyInput(form.valorBruto),
      valorLiquido: parseMoneyInput(form.valorLiquido),
      competencia,
      pagamentoEfetuado: false,
    };
    const res = await apiFetch(withUnidade('/api/notas-fiscais', unidadeSelecionada), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || 'Erro ao cadastrar processo/NF.');
    return data.id;
  };

  const handleEncaminhar = async () => {
    setError('');
    setSuccess('');
    if (!contratoOpt) {
      setError('Selecione o contrato (use a busca).');
      return;
    }
    if (items.length === 0) {
      setError(
        modo === 'existente'
          ? 'Anexe os PDFs novos para substituir a documentação desta NF.'
          : 'Adicione ao menos um PDF do processo.'
      );
      return;
    }
    const signatarios = montarSignatariosPayload();
    const cadeiaOk = (cadeiaPreview?.cadeia || []).length > 0 || signatarios.length > 0;
    if (!cadeiaOk) {
      setError(
        'Cadeia de assinatura vazia. Cadastre o fiscal do contrato (setor) e/ou configure responsáveis no Fluxo de Pagamento / signatários adicionais.'
      );
      return;
    }
    if (
      !window.confirm(
        modo === 'existente'
          ? 'Substituir a documentação desta NF (cadastro permanece), gerar o PDF na ordem 1, 2, 3… e reenviar ao fiscal?'
          : 'Cadastrar a NF, gerar o PDF do processo na ordem 1, 2, 3… e encaminhar ao fiscal?'
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const idNota = await criarNotaSeNecessario();
      setNotaId(String(idNota));

      const fd = new FormData();
      fd.append('notaId', String(idNota));
      fd.append('tipoDocumento', 'Relatório');
      fd.append('signatarios', JSON.stringify(signatarios));
      sortItemsByDocNumber(items).forEach((it) => fd.append('files', it.file, it.file.name));

      const token = getToken();
      const unidade = unidadeSelecionada?.codigo
        ? `?unidade=${encodeURIComponent(unidadeSelecionada.codigo)}`
        : '';
      const res = await fetch(
        `${API_CONFIG.BACKEND_URL}/api/arca/pdf/processo-pagamento/encaminhar${unidade}`,
        {
          method: 'POST',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          body: fd,
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Erro (${res.status}).`);

      try {
        const dl = await apiFetch(
          withUnidade(`/api/notas-fiscais/${idNota}/pdf-consolidado`, unidadeSelecionada)
        );
        if (dl.ok) {
          const blob = await dl.blob();
          baixarBlob(blob, data.pdfConsolidado?.nomeArquivo || `processo-${idNota}.pdf`);
        }
      } catch (_) {
        /* ignore */
      }

      const emailInfo = data.email?.para
        ? ` E-mail enviado a ${data.email.para} (${data.email.saudacao}).`
        : '';
      const compress =
        data.email?.compressao && data.email.compressao.compressedSize < data.email.compressao.originalSize
          ? ` PDF comprimido de ${Math.round(data.email.compressao.originalSize / 1024 / 1024)}MB para ${Math.round(data.email.compressao.compressedSize / 1024 / 1024)}MB.`
          : '';
      const warn = data.emailWarning ? ` Aviso: ${data.emailWarning}` : '';
      setSuccess(
        `${data.message || 'Processo encaminhado.'} Processo: ${
          data.numero_processo || '—'
        }.${emailInfo}${compress}${warn}`
      );
      setItems([]);
      setForm(emptyForm);
      setSignatariosSel([]);
      setEmailsExternos('');
      // refresh notas antes de trocar modo (evita Select out-of-range)
      const pathNotas = withUnidade(
        `/api/notas-fiscais/${encodeURIComponent(contratoSel)}`,
        unidadeSelecionada
      );
      const rn = await apiFetch(pathNotas);
      if (rn.ok) {
        const list = await rn.json();
        setNotas(Array.isArray(list) ? list : []);
        setNotaId(String(idNota));
        setModo('existente');
        const pathDocs = withUnidade(`/api/notas-fiscais/${idNota}/documentos`, unidadeSelecionada);
        const rd = await apiFetch(pathDocs);
        if (rd.ok) {
          const docs = await rd.json();
          const list = Array.isArray(docs) ? docs : [];
          setDocsExistentes(
            [...list].sort((a, b) => {
              const na = leadingDocNumber(a.nome_arquivo);
              const nb = leadingDocNumber(b.nome_arquivo);
              if (na !== nb) return na - nb;
              return String(a.nome_arquivo || '').localeCompare(String(b.nome_arquivo || ''), 'pt-BR', {
                numeric: true,
                sensitivity: 'base',
              });
            })
          );
        }
      }
    } catch (e) {
      console.error('[ArcaProcessoPagamento]', e);
      setError(e.message || 'Erro ao encaminhar processo.');
    } finally {
      setLoading(false);
    }
  };

  const anos = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2, y + 1];
  }, []);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', width: '100%' }}>
      <Alert severity="info" sx={{ mb: 2 }}>
        Remetente do e-mail: <strong>{user?.nome || user?.email || 'usuário logado'}</strong>. Busque
        o contrato por nome, nº, objeto/especialidade ou serviço médico.
      </Alert>

      <Stack spacing={2.5} sx={{ mb: 2.5 }}>
        <Autocomplete
          options={contratosFiltrados}
          loading={loadingContratos}
          value={contratoOpt}
          inputValue={contratoInput}
          onInputChange={(_, v) => setContratoInput(v)}
          onChange={(_, v) => {
            setContratoOpt(v);
            setNotaId('');
            setItems([]);
            setSuccess('');
            setForm(emptyForm);
          }}
          getOptionLabel={(o) => labelContrato(o)}
          isOptionEqualToValue={(a, b) => a?.numeroContrato === b?.numeroContrato}
          filterOptions={(x) => x}
          renderOption={(props, option) => (
            <li {...props} key={option.id || option.numeroContrato}>
              <Box sx={{ py: 0.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {option.numeroContrato} — {option.empresa || option.titulo}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[option.objeto, option.tipo, option.servicoMedico ? 'Serviço médico' : null, option.setor]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Buscar contrato"
              placeholder="Digite nome, nº do contrato, especialidade/objeto ou serviço médico…"
              helperText="A lista filtra enquanto você digita"
            />
          )}
          disabled={loading}
        />

        {contratoOpt && (
          <ToggleButtonGroup
            exclusive
            size="small"
            value={modo}
            onChange={(_, v) => {
              if (!v) return;
              setModo(v);
              setItems([]);
              setSuccess('');
              if (v === 'novo') setNotaId('');
            }}
          >
            <ToggleButton value="novo">
              <AddCircleOutlineIcon fontSize="small" sx={{ mr: 0.75 }} />
              Cadastrar novo processo
            </ToggleButton>
            <ToggleButton value="existente">Usar NF já cadastrada</ToggleButton>
          </ToggleButtonGroup>
        )}

        {contratoOpt && modo === 'novo' && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Dados do processo / nota fiscal
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              O sistema gera o <strong>nº do processo de pagamento</strong> ao salvar. Preencha a NF e
              os valores.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  label="Nº da nota fiscal"
                  value={form.numeroNotaFiscal}
                  onChange={(e) => setForm({ ...form, numeroNotaFiscal: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  required
                  type="date"
                  label="Data de emissão"
                  InputLabelProps={{ shrink: true }}
                  value={form.dataEmissao}
                  onChange={(e) => setForm({ ...form, dataEmissao: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="Data de recebimento"
                  InputLabelProps={{ shrink: true }}
                  value={form.dataRecebimento}
                  onChange={(e) => setForm({ ...form, dataRecebimento: e.target.value })}
                  disabled={loading}
                  helperText="Opcional — usa emissão se vazio"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required>
                  <InputLabel>Mês competência</InputLabel>
                  <Select
                    label="Mês competência"
                    value={form.mesCompetencia}
                    onChange={(e) => setForm({ ...form, mesCompetencia: e.target.value })}
                    disabled={loading}
                  >
                    {MESES_COMPETENCIA.map((m) => (
                      <MenuItem key={m} value={m}>
                        {m}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormControl fullWidth required>
                  <InputLabel>Ano competência</InputLabel>
                  <Select
                    label="Ano competência"
                    value={form.anoCompetencia}
                    onChange={(e) => setForm({ ...form, anoCompetencia: e.target.value })}
                    disabled={loading}
                  >
                    {anos.map((y) => (
                      <MenuItem key={y} value={String(y)}>
                        {y}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  required
                  label="Valor bruto"
                  placeholder="0,00"
                  value={form.valorBruto}
                  onChange={(e) => setForm({ ...form, valorBruto: e.target.value })}
                  disabled={loading}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  required
                  label="Valor líquido"
                  placeholder="0,00"
                  value={form.valorLiquido}
                  onChange={(e) => setForm({ ...form, valorLiquido: e.target.value })}
                  disabled={loading}
                />
              </Grid>
            </Grid>
          </Paper>
        )}

        {contratoOpt && modo === 'existente' && (
          <>
            <FormControl fullWidth size="small" disabled={loadingNotas || loading}>
              <InputLabel id="nota-label">Nota / Processo já cadastrado</InputLabel>
              <Select
                labelId="nota-label"
                label="Nota / Processo já cadastrado"
                value={notaId}
                onChange={(e) => {
                  setNotaId(e.target.value);
                  setItems([]);
                  setSuccess('');
                }}
              >
                <MenuItem value="">
                  <em>Selecione…</em>
                </MenuItem>
                {notas.map((n) => (
                  <MenuItem key={n.id} value={String(n.id)}>
                    {n.numero_processo || 'Sem processo'} · NF {n.numero_nota_fiscal || '—'} ·{' '}
                    {formatMoney(n.valor_liquido)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {notaSelecionada && (
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <LockOutlinedIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight={700}>
                    NF já cadastrada — será reaproveitada
                  </Typography>
                  <Chip size="small" label={notaSelecionada.status || '—'} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Processo: <strong>{notaSelecionada.numero_processo || '—'}</strong> · Emissão:{' '}
                  <strong>{formatDate(notaSelecionada.data_emissao)}</strong> · Bruto:{' '}
                  <strong>{formatMoney(notaSelecionada.valor_bruto)}</strong> · Líquido:{' '}
                  <strong>{formatMoney(notaSelecionada.valor_liquido)}</strong>
                  . Os PDFs novos <strong>substituem</strong> os anexos abaixo e entram na ordem 1, 2, 3…
                </Typography>
                <Divider sx={{ my: 1 }} />
                {loadingDocs ? (
                  <CircularProgress size={22} />
                ) : docsExistentes.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum documento anexado ainda.
                  </Typography>
                ) : (
                  <List dense disablePadding>
                    {docsExistentes.map((d) => (
                      <ListItem key={d.id} disableGutters>
                        <ListItemText
                          primary={d.nome_arquivo}
                          secondary={`${d.tipo_documento || 'Doc'}${
                            d.subtipo_documento ? ` · ${d.subtipo_documento}` : ''
                          }`}
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            )}
          </>
        )}

        {contratoOpt && (modo === 'novo' || notaId) && (
          <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
              Cadeia de assinatura digital (Fluxo de Pagamento)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              O fiscal do contrato entra automaticamente (ex.: Agimed → setor Manutenção → William).
              A etapa Financeiro usa o setor financeiro da unidade selecionada (coordenador/analista),
              não o diretor financeiro corporativo. Demais etapas seguem Configuração → Fluxos. Você
              pode acrescentar signatários extras abaixo.
            </Typography>
            {loadingCadeia ? (
              <Box display="flex" alignItems="center" gap={1} sx={{ mb: 2 }}>
                <CircularProgress size={18} />
                <Typography variant="caption">Montando cadeia…</Typography>
              </Box>
            ) : cadeiaPreview ? (
              <Box sx={{ mb: 2 }}>
                {cadeiaPreview.fluxo && (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                    Fluxo: <strong>{cadeiaPreview.fluxo.nome}</strong>
                    {cadeiaPreview.fiscalContrato?.email
                      ? ` · Fiscal: ${cadeiaPreview.fiscalContrato.nome || ''} <${cadeiaPreview.fiscalContrato.email}>`
                      : ' · Fiscal do contrato não encontrado no cadastro'}
                  </Typography>
                )}
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                  {(cadeiaPreview.cadeia || []).map((s, i) => (
                    <Chip
                      key={`${s.email}-${i}`}
                      size="small"
                      icon={<LockOutlinedIcon />}
                      label={`${i + 1}. ${s.nome || s.email}${s.etapaNome ? ` (${s.etapaNome})` : ''}`}
                      sx={{ fontWeight: 600 }}
                      color={s.origem === 'fiscal_contrato' ? 'warning' : 'default'}
                    />
                  ))}
                  {!(cadeiaPreview.cadeia || []).length && (
                    <Alert severity="warning" sx={{ width: '100%' }}>
                      Nenhum signatário resolvido. Configure o fiscal do setor do contrato ou os
                      responsáveis nas etapas do Fluxo de Pagamento.
                    </Alert>
                  )}
                </Stack>
              </Box>
            ) : null}
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Signatários adicionais (opcional)
            </Typography>
            <Autocomplete
              multiple
              options={userOptions}
              value={signatariosSel}
              onChange={(_, v) => setSignatariosSel(v)}
              getOptionLabel={(o) => o.label || o.nome || o.email || ''}
              isOptionEqualToValue={(a, b) => String(a.id) === String(b.id)}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    {...getTagProps({ index })}
                    key={option.id || option.email}
                    label={option.label || option.nome}
                    size="small"
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label="Usuários do sistema" placeholder="Digite nome ou e-mail" />
              )}
              disabled={loading}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="E-mails externos (separados por vírgula)"
              placeholder="E-mails externos (separados por vírgula)"
              value={emailsExternos}
              onChange={(e) => setEmailsExternos(e.target.value)}
              disabled={loading}
            />
          </Paper>
        )}
      </Stack>

      {contratoOpt && (modo === 'novo' || notaId) ? (
        <ArcaPdfStudioLayout
          title="Processo de pagamento"
          info={
            modo === 'existente'
              ? 'Anexe os PDFs novos (1. 2. 3. …). Eles substituem a documentação anterior desta NF. A ordem numérica é aplicada automaticamente.'
              : 'Anexe os PDFs do processo (1. 2. 3. …). A ordem crescente é aplicada automaticamente. Visualize, gire e encaminhe.'
          }
          limits={
            <>
              PDFs · até <strong>500 MB</strong> cada. Remetente:{' '}
              <strong>{user?.nome || user?.email || 'usuário logado'}</strong>.
            </>
          }
          error={error}
          onClearError={() => setError('')}
          onRotateError={setError}
          success={success}
          onClearSuccess={() => setSuccess('')}
          items={items}
          setItems={setItems}
          loading={loading}
          showBack
          dropzone={{
            accept: 'application/pdf,.pdf',
            multiple: true,
            maxSizeMb: 500,
            onFilesAdd: handleFilesAdd,
            label: 'Arraste os PDFs do processo ou clique',
            hint: 'Nota fiscal, atesto, relatórios etc. · máx. 500MB cada',
          }}
          actionLabel="Gerar e encaminhar"
          actionLoadingLabel="Gerando e encaminhando…"
          actionStartIcon={<SendOutlinedIcon />}
          onAction={handleEncaminhar}
          actionDisabled={
            !contratoOpt ||
            montarSignatariosPayload().length === 0 ||
            (modo === 'existente' && (!notaId || items.length === 0)) ||
            (modo === 'novo' &&
              (!form.numeroNotaFiscal ||
                !form.dataEmissao ||
                !form.mesCompetencia ||
                !form.valorLiquido))
          }
        />
      ) : (
        <Alert severity="warning">
          Selecione o contrato e complete o cadastro (ou escolha uma NF existente) para anexar os
          PDFs.
          {error ? (
            <Typography variant="body2" sx={{ mt: 1 }}>
              {error}
            </Typography>
          ) : null}
        </Alert>
      )}
    </Box>
  );
}
